import { nanoid } from 'nanoid'
import { useCallback } from 'react'
import CPTCEModalEditor from '../CPTCEModalEditor'
import useDataFromCPTME from '../CPTCEModalEditor/useDataFromCPTME'
import { ComponentWithPermissions } from '../../../../../../../../../../../baseComponents/CreateRoutering/types'
import { ModalCreateEditStructureLayout } from '../../../../../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import TwinIcon, { TwinIconProp } from '../../../../../../../../../../../baseComponents/TwinIcon'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { PdfConfigAllModulesVariablesMap, PdfConfigTemplateAllModulesKey } from '../../../../../types'
import usePdfSingleModuleData from '../../../../../usePdfSingleModuleData'
import { PdfConfigTemplateTabProps } from '../../../../types'
import { ModulePdfConfigParent } from '../types'
import TwinForm from '../../../../../../../../../../../forms/TwinForm'
import { BeforeSubmitHandler } from '../../../../../../../../../../../forms/TwinForm/types'
import { addAntiUnsaveElement } from '../../../../../../../../../../../utils/reducers/reduxDispatch'

type CPTModalCreateProps = CPTModalCreateLogicProps & ComponentWithPermissions & {
    modules: PdfConfigAllModulesVariablesMap
    setMyModule: React.Dispatch<React.SetStateAction<ModulePdfConfigParent>>
}

const CPTModalCreate: React.FC<CPTModalCreateProps> = ({ myModule, setMyModule, modules, userPermissions, ...rest }) => {
    const { prevSubmitAddModule } = useCPTModalCreate({ ...rest, myModule })
    const { t } = useTwinTranslation()
    const { singleModuleData } = usePdfSingleModuleData()
    let renderThis: JSX.Element | null = null
    if (!myModule) {
        const renderThisInner: JSX.Element[] = []
        for (const key in modules) {
            const element = key as PdfConfigTemplateAllModulesKey
            renderThisInner.push(<ModuleAdder onClick={() => setMyModule({ component: element })} key={element} icon={singleModuleData[element].icon} text={singleModuleData[element].text} />)
        }
        renderThis = <div className='grid grid-cols-3 gap-20'>{renderThisInner}</div>
    } else {
        renderThis = <CPTCEModalEditor pdfConfigElement={{ extraProps: {} as any, component: myModule, cssConfig: {}, id: '' }} userPermissions={userPermissions} moduleVars={modules[myModule] || []} setMyModule={setMyModule} documentType={rest.config.type}/>
    }
    return (
        <TwinForm beforeSubmitHandler={prevSubmitAddModule} className='flex flex-auto flex-col'>
            <ModalCreateEditStructureLayout translations={{ button: t('save', 'Guardar'), title: t('addModule', 'Añadir módulo') }} loading={false} haveButtonPermissions={myModule !== null} className='modal_negative_margin flex flex-col'>
                <div className='overflow-auto flex flex-auto flex-col'>
                    {renderThis}
                </div>
            </ModalCreateEditStructureLayout>
        </TwinForm>
    )
}

interface CPTModalCreateLogicProps extends PdfConfigTemplateTabProps {
    modifyKey: number
    myModule: PdfConfigTemplateAllModulesKey | null
}

const useCPTModalCreate = ({ myModule, setOpenModal, modifyKey, setConfig }: CPTModalCreateLogicProps) => {
    const {beforeSubmitPrev} = useDataFromCPTME()
    const prevSubmitAddModule: BeforeSubmitHandler = useCallback((vals) => {
        addAntiUnsaveElement(['pdf_template_changes', '{}'])
        const { cssConfig, extraProps } = beforeSubmitPrev(vals)
        if (myModule) {
            const splitKey = String(modifyKey).split(',')
            const myPageKey = splitKey[0]
            const mySectionKey = splitKey[1]
            setConfig((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                copy.pages[myPageKey].sections[mySectionKey].elements.push({
                    component: myModule,
                    cssConfig,
                    extraProps,
                    id: copy.pages[myPageKey].sections[mySectionKey].elements.length + nanoid(),
                })
                return { ...copy }
            })
            setOpenModal(null)
        }
        return false
    }, [myModule, modifyKey, setConfig, setOpenModal, beforeSubmitPrev])
    return { myModule, prevSubmitAddModule, beforeSubmitPrev }
}


interface ModuleAdderProps {
    icon: TwinIconProp
    text: string
    onClick: () => void
}

const ModuleAdder: React.FC<ModuleAdderProps> = ({ icon, text, onClick }) => {
    return (
        <div className='flex flex-col bg-gray-F7 p-20 rounded-8 justify-center items-center cursor-pointer' onClick={onClick}>
            <TwinIcon icon={icon} className='mb-10' style={{ height: 20 }} />
            <span>{text}</span>
        </div>
    )
}

export default CPTModalCreate